import { datadogLogs } from "@datadog/browser-logs";
import { SpanKind, SpanStatusCode, trace } from "@opentelemetry/api";

import { Logger } from "#lib/logging.js";
import { activeContext } from "#lib/opentelemetry.js";
import { PUBLIC_DD_CLIENT_TOKEN, PUBLIC_DD_ENV, PUBLIC_DD_SERVICE, PUBLIC_DD_VERSION } from "$app/env/public";
import { navigating } from "$app/state";
import type { Exception, Span } from "@opentelemetry/api";

import "./wasm_exec.js";
import type { HandleClientError } from "@sveltejs/kit/hooks";

const tracer = trace.getTracer("svelte", "0.1.0");

function isConsumerRoute(pathname: string) {
	return pathname.startsWith("/s/") || pathname.startsWith("/v/");
}

function initializeDatadog() {
	if (PUBLIC_DD_CLIENT_TOKEN) {
		datadogLogs.init({
			env: PUBLIC_DD_ENV,
			clientToken: PUBLIC_DD_CLIENT_TOKEN,
			service: PUBLIC_DD_SERVICE,
			version: PUBLIC_DD_VERSION,
			forwardErrorsToLogs: true,
			sessionSampleRate: 100,
			beforeSend(event, context) {
				// Silence @xyflow/svelte's known ResizeObserver loop warning (xyflow/xyflow#4792 — "safely ignore"). Pass-through in dev so regressions in our own code stay visible.
				if ((PUBLIC_DD_ENV === "prod" || PUBLIC_DD_ENV === "stg") && event?.message?.includes("ResizeObserver loop")) {
					return false;
				}
				if (context instanceof Error && (event?.message === "Cannot read properties of undefined (reading 'ownerDocument')" || event?.message.includes("Fetch error GET chrome-extension://"))) {
					return false;
				} else {
					return true;
				}
			},
		});
	}
	if (process.env.NODE_ENV === "development") {
		datadogLogs.logger.setHandler("console");
	} else {
		datadogLogs.logger.setHandler("http");
	}
}

async function loadWasm(): Promise<void> {
	return tracer.startActiveSpan(
		"wasm/load",
		{
			kind: SpanKind.CLIENT,
		},
		activeContext(),
		async (span: Span) => {
			try {
				const go = new window.Go();
				const { instance } = await WebAssembly.instantiateStreaming(fetch("/wasm/main.wasm"), go.importObject);
				void go.run(instance);
			} catch (e) {
				span.recordException(e as Exception);
				span.setStatus({ code: SpanStatusCode.ERROR });
				Logger.error("Failed to load WASM", { err: e });
				throw e;
			} finally {
				span.end();
			}
		},
	);
}

// Recover from failed dynamic-import chunk loads (Vite's vite:preloadError). preventDefault makes Vite resolve the
// failed loader to undefined instead of rejecting, which crashes SvelteKit's runtime, so only call it on a path
// that then reloads. Otherwise let Vite reject and SvelteKit renders its error page.
export function handlePreloadErrors() {
	let reloading = false;

	window.addEventListener("vite:preloadError", event => {
		if (reloading) {
			event.preventDefault();
			return;
		}
		// A navigation aborted the import. Silence it and let SvelteKit finish navigating.
		if (navigating.to) {
			event.preventDefault();
			return;
		}
		// Reload at most once per 10s to avoid a loop when a chunk is genuinely gone. If sessionStorage is
		// unavailable (private mode, blocked, or full) we cannot track that, so leave the reject in place and let
		// SvelteKit render an error page rather than risk a crash or a reload loop. Record the time before
		// preventDefault so a write failure never leaves us silenced without a reload.
		const reloadedAtKey = "vite-preload-reloaded-at";
		try {
			const lastReloadedAt = Number(window.sessionStorage.getItem(reloadedAtKey) ?? 0);
			if (Date.now() - lastReloadedAt <= 10_000) {
				return;
			}
			window.sessionStorage.setItem(reloadedAtKey, String(Date.now()));
		} catch {
			return;
		}
		event.preventDefault();
		reloading = true;
		Logger.warn("Reloading to recover a failed chunk load", { err: (event as Event & { payload?: unknown }).payload });
		window.location.reload();
	});
}

export async function init() {
	// Load our otel instrumentation
	// instrumentation.load();

	// Recover from stale dynamically-imported chunk failures on all routes, including consumer scan pages
	handlePreloadErrors();

	if (!isConsumerRoute(window.location.pathname)) {
		// Initialize the datadog browser logs
		initializeDatadog();

		// Load the WASM binary
		await loadWasm();
	}
}

export function handleError({ kind, error, event }: Parameters<HandleClientError>[0]): ReturnType<HandleClientError> {
	if (kind !== "unknown") {
		return error;
	}
	Logger.error(error?.toString() ?? "Unknown error", { err: error, event });
	return {
		message: error?.toString() ?? "",
	};
}
